import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import posthog from 'posthog-js'
import {
  buildAccountInactiveLoginPath,
  getSession,
  getUserMenus,
  getUserPermissions,
  getUserRoles,
  isStaleAuthUserError,
  signInWithGoogle,
  signOut,
  signOutIfInactive,
  syncUserProfile
} from '../services/authSupabase'
import { getLogger } from '../../../../core/debuggers'
import {
  clearUserProfile,
  hydrateUserProfile
}
from '../../../../containers/UserProfile/UserProfile.slice'
import {
  clearAccessControl,
  setMenus,
  setPermissions,
  setRoles
} from '../../../../containers/AccessControlList/acl.slice'
import { resetNotificationsState } from '../../../../containers/Notification/Notification.slice'

const logger = getLogger('authSupabase.js')

/**
 * Estado inicial del auth slice
*/
const initialState = {
  error: null,
  isAuthenticated: false,
  loading: false,
  initialLoading: true, // ✅ nuevo: indica que la sesión aún se verifica
  user: null,
}

/**
 * Sign in with Google
*/
export const onSignInWithGoogle = createAsyncThunk(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }
) => {
  try {
    return await signInWithGoogle()
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

/**
 * Cargar sesión actual y perfil del usuario
 * (Usado por SessionWatcher y al iniciar la app)
 */
export const onLoadUserAndProfile = createAsyncThunk(
  'auth/loadUserAndProfile',
  async (_, { dispatch, rejectWithValue }) => {
  try {
    const { session, error } = await getSession()

    if (error) {
      return rejectWithValue(error?.message)
    }

    if (!session?.user) {
      dispatch(clearUserProfile())
      dispatch(clearAccessControl())
      dispatch(resetNotificationsState())

      return {
        user: null
      }
    }

    const user = session.user

    // Load domain data
    const profile = await syncUserProfile(user)

    if (await signOutIfInactive(profile?.status)) {
      posthog.capture('account_inactive_blocked')
      dispatch(clearUserProfile())
      dispatch(clearAccessControl())
      dispatch(resetNotificationsState())
      if (typeof window !== 'undefined') {
        window.location.replace(buildAccountInactiveLoginPath())
      }
      return { user: null }
    }

    const roles = await getUserRoles(user.id)
    const menus = await getUserMenus(user.id)
    const permissions = await getUserPermissions(user.id)

    // 🔁 Route data to owners
    dispatch(hydrateUserProfile(profile))
    dispatch(setRoles(roles))
    dispatch(setMenus(menus))
    dispatch(setPermissions(permissions))

    return { user }
  } catch (error) {
    if (isStaleAuthUserError(error)) {
      dispatch(clearUserProfile())
      dispatch(clearAccessControl())
      dispatch(resetNotificationsState())
      return { user: null }
    }
    return rejectWithValue(error?.message)
  }
})

/**
 * Cerrar sesión del usuario
 */
export const onSignOut = createAsyncThunk(
  'auth/signOut',
  async (_, { dispatch, rejectWithValue }) => {
  try {
    localStorage.removeItem('supabase.auth.token')
    localStorage.removeItem('supabase.auth.token#')
    
    sessionStorage.clear()
    
    Object.keys(localStorage)
      .filter((key) => key.startsWith('sb-') && key.endsWith('-auth-token'))
      .forEach((key) => localStorage.removeItem(key))

    await signOut()

    dispatch(clearUserProfile())
    dispatch(clearAccessControl())
    dispatch(resetNotificationsState())

    logger.debug('Logout completado exitosamente.')

    return {
      user: null,
      profile: null,
      isAuthenticated: false
    }
  } catch (err) {
    logger.error('⚠️ Excepción inesperada en signOut:', err)
    return rejectWithValue(err.message)
  }
})

/**
 * Slice principal de autenticación Supabase
 */
export const authSlice = createSlice({
  name: 'authSupa',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // --- Sign in with Google ---
      .addCase(onSignInWithGoogle.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(onSignInWithGoogle.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload?.user || null
        state.isAuthenticated = !!state.user
      })
      .addCase(onSignInWithGoogle.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error al iniciar sesión con Google'
      })

      // --- Cargar sesión + perfil ---
      .addCase(onLoadUserAndProfile.pending, (state) => {
        state.error = null
      })
      .addCase(onLoadUserAndProfile.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.isAuthenticated = !!action.payload.user
        state.initialLoading = false
      })
      .addCase(onLoadUserAndProfile.rejected, (state, action) => {
        state.user = null
        state.isAuthenticated = false
        state.initialLoading = false
        state.error = action.payload
      })

      // --- Cerrar sesión ---
      .addCase(onSignOut.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(onSignOut.fulfilled, (state) => {
        state.loading = false
        state.user = null
        state.isAuthenticated = false
      })
      .addCase(onSignOut.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Error al cerrar sesión'
      })
  }
})

export default authSlice.reducer
